import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import { SetuMark } from "@/components/Logo";
import {
  ArrowUpRight, ArrowLeft, ScanEye, MessageSquareText, FileText, Mic,
  ShieldCheck, QrCode, CloudSun, Fingerprint, CheckCircle2, MessageCircle, WifiOff,
} from "lucide-react";

const FEATURES = [
  {
    slug: "photo-inspector",
    icon: ScanEye,
    title: "AI Photo Inspector",
    tagline: "Turn any site photo into a compliance report in 6 seconds.",
    problem:
      "Site engineers waste 2–3 hours a day walking around with a clipboard, missing hazards, incomplete PPE checks and rework triggered by defects nobody spotted until the reviewer flagged them.",
    how: [
      "Upload a photo from your phone or dashboard",
      "AI vision analyses hazards, PPE compliance, defects and progress signals",
      "Get a labelled report with a numeric confidence score",
      "Attach the report to a daily log or share directly to WhatsApp",
    ],
    outcomes: [
      "Detect PPE violations before they become an incident",
      "Catch rebar spacing, honeycombing and cover errors early",
      "Reduce hazard-to-fix time from days to hours",
    ],
    who: "Site Engineers · Safety Officers · Project Managers",
  },
  {
    slug: "civil-ai",
    icon: MessageSquareText,
    title: "Civil AI Expert",
    tagline: "Your on-demand senior civil engineer, in Hindi or English.",
    problem:
      "Junior engineers get stuck on M-grade concrete mix ratios, rebar cover, slump values, and code compliance — and there's no senior available on-site to answer instantly.",
    how: [
      "Type or speak your question",
      "AI responds with citation-quality answers referencing IS, ACI, BS and Eurocode standards",
      "Follow-up in the same conversation to drill deeper",
      "Save answers to project notes",
    ],
    outcomes: [
      "Instant guidance on structural, material and safety questions",
      "Reduce mistakes made by junior staff",
      "Standardise decisions across multiple sites",
    ],
    who: "Junior Engineers · Site Supervisors · QA/QC Inspectors",
  },
  {
    slug: "daily-reports",
    icon: FileText,
    title: "Auto Daily Reports",
    tagline: "A polished, printable DPR — from bullet notes in 90 seconds.",
    problem:
      "Daily Progress Reports are legally required but engineers hate writing them. Most DPRs are late, inconsistent, or missing sections — creating audit and payment risks.",
    how: [
      "Enter labor count, equipment, activities, weather and any incidents",
      "AI generates a full 11-section professional DPR (executive summary → tomorrow's plan)",
      "Copy, download as .md, or print — ready to email the owner",
      "History is retained for compliance audit",
    ],
    outcomes: [
      "Never miss a DPR again",
      "Consistent format across all projects and engineers",
      "Faster owner sign-off; smoother billing milestones",
    ],
    who: "Site Engineers · Project Managers · Contractors",
  },
  {
    slug: "attendance",
    icon: Fingerprint,
    title: "Face + GPS Attendance",
    tagline: "Fraud-proof labour attendance. Face on-device, GPS every punch.",
    problem:
      "Contractor headcount fraud (ghost workers, buddy punching) drains 8–15% of every payroll. Manual attendance registers are trivially fudged.",
    how: [
      "Register each worker once — face descriptor computed on the device (photo never leaves the phone)",
      "At the kiosk, worker takes a selfie; face-match runs locally + verified server-side",
      "GPS coordinates + resolved address are captured on every punch",
      "History shows matched/flagged status and match distance",
    ],
    outcomes: [
      "Eliminate buddy punching and ghost workers",
      "Automate payroll based on real hours",
      "Location-proof for insurance and compliance audits",
    ],
    who: "Contractors · HR / Payroll · Site Supervisors",
  },
  {
    slug: "voice-logs",
    icon: Mic,
    title: "Voice Site Logs",
    tagline: "Speak your update. Get a searchable transcript instantly.",
    problem:
      "Foremen and mestris are more comfortable speaking than typing — especially in Hindi/regional languages. Voice notes get lost in WhatsApp chats and never make it into official logs.",
    how: [
      "Tap record, speak your update in Hindi or English",
      "AI transcribes and structures the note",
      "Log is attached to the current project and searchable",
      "Automatically referenced when tomorrow's DPR is generated",
    ],
    outcomes: [
      "Zero-friction site logging",
      "Regional language accessibility",
      "Nothing important gets lost between shifts",
    ],
    who: "Foremen · Mestris · Site Engineers",
  },
  {
    slug: "safety",
    icon: ShieldCheck,
    title: "Safety & Incidents",
    tagline: "Real-time incident feed. Severity triage. Audit-ready records.",
    problem:
      "Safety incidents are logged in paper registers that get lost, damaged, or 'edited' after the fact. Regulators want traceable, timestamped, immutable records.",
    how: [
      "Report an incident with type, severity, location and description",
      "Live marquee shows open incidents across the site",
      "Track resolution end-to-end",
      "Full audit log — every action time-stamped and attributed",
    ],
    outcomes: [
      "Immediate visibility on active hazards",
      "Compliance-ready incident register",
      "Lower insurance premiums through demonstrated safety culture",
    ],
    who: "Safety Officers · Site Managers · Compliance Auditors",
  },
  {
    slug: "equipment",
    icon: QrCode,
    title: "QR Equipment Registry",
    tagline: "Every tool scannable. Every check-in logged.",
    problem:
      "Tool theft, misplacement, and unnecessary rentals cost mid-size sites ₹40k–₹2L per month. Excel registers can't tell you where a specific drill is right now.",
    how: [
      "Register equipment; system auto-generates a unique QR code",
      "Print and stick the code on the tool",
      "Scan to check in, check out, or view history",
      "Filter by status (available / in-use / maintenance)",
    ],
    outcomes: [
      "Zero tool theft — every asset traceable",
      "Cut unnecessary rental hire",
      "Predictive maintenance from usage patterns",
    ],
    who: "Store Managers · Site Supervisors · Contractors",
  },
  {
    slug: "weather",
    icon: CloudSun,
    title: "Weather Scheduler",
    tagline: "Plan around the sky. Not around it after it rains.",
    problem:
      "One unexpected rainy day can push concrete pours by a week and cost lakhs in idle equipment. Yet most PMs check the weather when it's already raining.",
    how: [
      "Pin the site's location once",
      "See a 7-day workability forecast overlay on your plan",
      "AI marks WORK / STOP days based on temperature, wind, humidity, rain",
      "Reschedule critical activities away from unfavourable windows",
    ],
    outcomes: [
      "Fewer wasted deployments",
      "Better concrete quality — no pouring in unfit weather",
      "Owner sees you plan proactively, not reactively",
    ],
    who: "Project Managers · Site Engineers",
  },
  {
    slug: "offline-mode",
    icon: WifiOff,
    title: "Offline-First Attendance",
    tagline: "Zero network on-site? No problem. Punches work anywhere — sync when back online.",
    problem:
      "80% of Indian construction sites — especially highways, tunnels, hydro projects, and remote residential builds — have patchy or zero mobile network. Competitor apps stop working the moment the bars drop, forcing contractors back to paper registers and losing the fraud-proof face + GPS audit trail. This is where every other 'construction app' fails.",
    how: [
      "Face recognition model runs 100% on-device (no server call needed for the match)",
      "GPS captured natively from the browser's location API",
      "When network is down, punches are securely queued in on-device encrypted storage",
      "A live 'Offline Mode' banner shows how many punches are pending",
      "The moment network returns, every queued punch auto-syncs to the server with original timestamps intact",
      "Manual 'Sync now' button also available",
    ],
    outcomes: [
      "Attendance works on hydro dams, tunnel projects, and remote sites where competitors fail",
      "Zero data loss — punches survive browser refresh, phone lock, and app close",
      "Payroll reconciliation stays accurate even with intermittent connectivity",
      "Legal audit trail with original punch time (not sync time)",
    ],
    who: "Contractors on remote projects · Highway & tunnel builders · Rural construction · Hydro projects",
  },
];

export default function Features() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen grain-bg" data-testid="features-page">
      <header className="border-b border-border/60 bg-background/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 h-14 sm:h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-8 h-8 rounded-md bg-primary grid place-items-center text-primary-foreground shrink-0"><SetuMark size={22} strokeWidth={2.6} /></div>
            <span className="font-heading text-base sm:text-lg tracking-tight">Setubuild.OS</span>
          </Link>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
            <Link to="/signup" className="btn-sharp inline-flex items-center gap-1 sm:gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-foreground text-background whitespace-nowrap">
              Start free <ArrowUpRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            </Link>
          </div>
        </div>
      </header>

      <section className="border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 py-12 sm:py-16 md:py-24">
          <Link to="/" className="mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-primary inline-flex items-center gap-1.5" data-testid="features-back">
            <ArrowLeft className="h-3 w-3" /> Back to home
          </Link>
          <h1 className="font-display text-4xl sm:text-5xl md:text-7xl leading-[1.05] sm:leading-[0.95] mt-4 sm:mt-6">
            Nine features. <span className="font-display-italic text-primary">One command centre.</span>
          </h1>
          <p className="mt-6 sm:mt-8 text-base sm:text-lg text-muted-foreground max-w-2xl">
            Everything Setubuild.OS does — explained honestly. What problem it solves, how it works, and what changes for you.
          </p>
          <div className="mt-8 sm:mt-10 flex flex-wrap gap-2">
            {FEATURES.map((f) => (
              <a key={f.slug} href={`#${f.slug}`} data-testid={`toc-${f.slug}`}
                className="mono text-[10px] uppercase tracking-[0.15em] border border-border rounded-full px-3 py-1.5 hover:bg-muted transition-colors">
                {f.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 py-12 sm:py-16 md:py-24 space-y-16 sm:space-y-24">
        {FEATURES.map((f, i) => {
          const Icon = f.icon;
          return (
            <section key={f.slug} id={f.slug} data-testid={`feature-detail-${f.slug}`} className="scroll-mt-20 sm:scroll-mt-24">
              <div className="flex items-start justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="flex-1 min-w-0">
                                    <h2 className="font-display text-3xl sm:text-4xl md:text-5xl mt-2 leading-tight">{f.title}</h2>
                  <p className="mt-2 sm:mt-3 text-base sm:text-lg text-primary italic">{f.tagline}</p>
                </div>
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-primary/10 grid place-items-center shrink-0">
                  <Icon className="h-6 w-6 sm:h-7 sm:w-7 text-primary" strokeWidth={1.5} />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
                <div>
                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">The Problem</div>
                  <p className="text-muted-foreground leading-relaxed">{f.problem}</p>

                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3 mt-8">Built for</div>
                  <p className="text-sm">{f.who}</p>
                </div>

                <div>
                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">How it Works</div>
                  <ol className="space-y-2 text-sm">
                    {f.how.map((h, hi) => (
                      <li key={hi} className="flex gap-3">
                        <span className="mono text-[10px] text-primary shrink-0 mt-1">{String(hi + 1).padStart(2, "0")}</span>
                        <span>{h}</span>
                      </li>
                    ))}
                  </ol>

                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3 mt-8">Outcomes</div>
                  <ul className="space-y-2 text-sm">
                    {f.outcomes.map((o, oi) => (
                      <li key={oi} className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" strokeWidth={2} />
                        <span>{o}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-8 rule-ornament"><span className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Feature end</span></div>
            </section>
          );
        })}
      </div>

      <section className="border-t border-border">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-24 text-center">
          <h2 className="font-display text-4xl md:text-6xl leading-tight">
            See it working <span className="font-display-italic text-primary">on your site.</span>
          </h2>
          <p className="mt-6 text-muted-foreground">14-day free trial. No credit card. Cancel anytime.</p>
          <Link to="/signup" className="btn-sharp mt-8 inline-flex items-center gap-2 bg-foreground text-background px-8 py-3.5 font-medium">
            Start free trial <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 flex flex-wrap items-center justify-between gap-4">
          <div className="mono text-xs text-muted-foreground">© {new Date().getFullYear()} SetuBuild Technologies. All rights reserved.</div>
          <div className="flex gap-5 mono text-[10px] uppercase tracking-[0.2em]">
            <Link to="/privacy" className="text-muted-foreground hover:text-foreground">Privacy</Link>
            <Link to="/terms" className="text-muted-foreground hover:text-foreground">Terms</Link>
            <Link to="/refund" className="text-muted-foreground hover:text-foreground">Refund</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
